import { FieldPath, FieldValues, useFormContext, useWatch } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../design-system/form/form-primitives';
import { Select } from '../../../../design-system/select/select';
import { Stack } from '../../../../design-system/stack/stack';
import { cn } from '../../../../utils/cn';
import { SECURITY_QUESTION_OPTIONS } from '../../utils/kba-question-labels';

/**
 * Generic question option type for the dropdown.
 * Use SecurityQuestionOption from kba-question-labels.ts for actual KBA questions.
 */
export type QuestionOption = {
  value: string;
  label: string;
  key: string;
};

type SecurityQuestionFieldProps<TFieldValues extends FieldValues> = {
  questionFieldName: FieldPath<TFieldValues>;
  answerFieldName: FieldPath<TFieldValues>;
  index: number;
  /** Custom question options for testing/stories. If not provided, uses default KBA questions. */
  questionOptions?: QuestionOption[];
};

/**
 * A combined form field for selecting a security question and providing an answer.
 * The answer field is revealed once a question is selected.
 *
 * @param props.questionFieldName - Form field name for the question type
 * @param props.answerFieldName - Form field name for the answer
 * @param props.index - Index of this question in the array (for labeling)
 *
 * @example
 * ```tsx
 * <SecurityQuestionField
 *   questionFieldName="knowledgeBase.0.type"
 *   answerFieldName="knowledgeBase.0.answer"
 *   index={0}
 * />
 * ```
 */
export const SecurityQuestionField = <TFieldValues extends FieldValues>({
  questionFieldName,
  answerFieldName,
  index,
  questionOptions = SECURITY_QUESTION_OPTIONS,
}: SecurityQuestionFieldProps<TFieldValues>) => {
  const { control } = useFormContext<TFieldValues>();
  const selectedQuestion = useWatch({ control, name: questionFieldName });

  return (
    <Stack space={4} align="stretch">
      <FormField
        control={control}
        name={questionFieldName}
        render={({ field, fieldState }) => {
          return (
            <FormItem onBlur={field.onBlur}>
              <Stack space={1} align="stretch">
                <FormControl>
                  <Select
                    aria-label={`Security question ${index + 1}`}
                    required
                    placeholder="Choose question..."
                    triggerLabel={
                      <FormLabel className="text-xs font-medium text-muted-foreground">Security question</FormLabel>
                    }
                    value={field.value}
                    onValueChange={field.onChange}
                    triggerClassName={cn(
                      'rounded-lg border border-border bg-card px-4 py-3 text-base font-medium text-foreground',
                      { 'border-destructive': !!fieldState.error },
                    )}
                    isTriggerFullWidth
                  >
                    {questionOptions.map((option) => (
                      <Select.Item key={option.key} value={option.value}>
                        <span className="block max-w-[280px] text-left leading-snug whitespace-normal">
                          {option.label}
                        </span>
                      </Select.Item>
                    ))}
                  </Select>
                </FormControl>

                <FormMessage className="w-full text-left text-destructive" />
              </Stack>
            </FormItem>
          );
        }}
      />

      {selectedQuestion && (
        <FormField
          control={control}
          name={answerFieldName}
          render={({ field, fieldState }) => {
            return (
              <Stack space={1} align="stretch">
                <FormItem
                  className={cn(
                    'rounded-lg border border-border bg-card px-4 py-3 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:outline-hidden',
                    { 'border-destructive': !!fieldState.error },
                  )}
                >
                  <Stack space={1} align="stretch">
                    <FormLabel className="text-xs font-medium text-muted-foreground">Your answer</FormLabel>

                    <FormControl>
                      <input
                        type="text"
                        placeholder=""
                        className="font-base w-full font-medium text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden"
                        aria-required
                        maxLength={45}
                        {...field}
                        value={field.value ?? ''}
                      />
                    </FormControl>
                  </Stack>
                </FormItem>

                <FormMessage className="w-full text-left text-destructive" />
              </Stack>
            );
          }}
        />
      )}
    </Stack>
  );
};
